import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { BarInterval } from '@/services/enums';
import { klineDataService } from '../services/klineDataService';
import { klineTaskService } from '../services/klineTaskService';
import { useToastStore } from '../stores/useToastStore';
import { markdownToHtml } from '../utils/markdownUtils';
import KlineAIMonitorDialog from './KlineAIMonitorDialog';
import SelectDropdown from './SelectDropdown';
import { adminService, SystemPromptInfo } from '../services/adminService';
import { estimateTokenCount } from './MyMonitors/utils';
import { LogSource } from '@/services/enums';

interface KlineAIPromptBuilderProps {
  symbol: string;
  bar: BarInterval;
  visibleRange: { from: number; to: number } | null;
}

const KlineAIPromptBuilder: React.FC<KlineAIPromptBuilderProps> = ({ symbol, bar, visibleRange }) => {
  const showToast = useToastStore((state) => state.show);
  const [promptLoading, setPromptLoading] = useState(false);
  const [clearCacheLoading, setClearCacheLoading] = useState(false);
  const [aiPrompt, setAiPrompt] = useState<string>('');
  const [systemPrompt, setSystemPrompt] = useState<string>('');
  const [userPrompt, setUserPrompt] = useState<string>('');
  const [promptTab, setPromptTab] = useState<LogSource>('user');
  const [systemPrompts, setSystemPrompts] = useState<SystemPromptInfo[]>([]);
  const [selectedSystemPromptId, setSelectedSystemPromptId] = useState<number | undefined>(undefined);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [promptTaskId, setPromptTaskId] = useState<string | null>(null);
  const [showMonitorDialog, setShowMonitorDialog] = useState(false);
  const [currentPromptHash, setCurrentPromptHash] = useState<string | null>(null);
  const lastPromptRangeRef = useRef<{ from: number; to: number } | null>(null);
  const aiPromptRef = useRef<string>('');
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const systemPromptsLoadedRef = useRef(false); // 防止重复加载系统提示词

  // 缓存键生成函数（只使用 symbol + bar 作为 key）
  const getCacheKey = () => {
    if (symbol && bar) {
      return `kline_analysis_prompt_${symbol}_${bar}`;
    }
    return null;
  };

  // 从缓存加载数据（只使用 symbol + bar 作为 key）
  const loadFromCache = () => {
    try {
      const cacheKey = getCacheKey();
      if (!cacheKey) return null;

      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const data = JSON.parse(cached);
        // 检查缓存是否过期（7天）
        const cacheTime = data.timestamp || 0;
        const now = Date.now();
        const sevenDays = 7 * 24 * 60 * 60 * 1000;

        if (now - cacheTime < sevenDays) {
          return data;
        } else {
          // 缓存过期，删除
          localStorage.removeItem(cacheKey);
        }
      }
    } catch (error) {
      console.error('加载缓存失败:', error);
    }
    return null;
  };

  // 保存到缓存（只使用 symbol + bar 作为 key）
  // 可以传入完整的结构体或部分数据
  // 会合并到已有缓存中，而不是完全替换
  const saveToCache = (data: any, promptOverride?: string) => {
    try {
      const cacheKey = getCacheKey();
      if (!cacheKey) return;

      // 先加载已有缓存（如果有）
      const existingCache = loadFromCache();
      const existingData = existingCache || {};

      // 合并数据：新数据会覆盖旧数据，但保留旧数据中的其他字段
      const cacheData = {
        ...existingData, // 保留已有数据
        ...data, // 新数据覆盖旧数据
        // 优先使用新数据中的 prompt，否则使用 promptOverride，最后使用 aiPrompt
        userPrompt: data.userPrompt || promptOverride || existingData.userPrompt || aiPrompt,
        timestamp: Date.now(),
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error('保存缓存失败:', error);
    }
  };

  // 根据symbol、bar查找缓存（只使用 symbol + bar 作为 key）
  const findCachedData = useCallback(() => {
    if (!symbol || !bar) return null;

    try {
      // 直接使用 symbol+bar 作为缓存键查找
      const cacheKey = getCacheKey();
      if (!cacheKey) return null;

      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const data = JSON.parse(cached);
          // 检查缓存是否过期（7天）
          const cacheTime = data.timestamp || 0;
          const now = Date.now();
          const sevenDays = 7 * 24 * 60 * 60 * 1000;

          if (now - cacheTime < sevenDays) {
            // 返回缓存数据
            return {
              key: cacheKey,
              data,
              range: data.range || undefined,
            };
          } else {
            // 缓存过期，删除
            localStorage.removeItem(cacheKey);
          }
        } catch (e) {
          console.error('解析缓存数据失败:', e);
        }
      }
    } catch (error) {
      console.error('查找缓存失败:', error);
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, bar]);

  // 轮询提示词分析任务状态的通用函数
  const pollPromptAnalysisStatus = useCallback((taskId: string, silent: boolean = false) => {
    // 清除之前的轮询
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }

    // 开始轮询时设置分析中状态
    setAnalyzing(true);

    const delayAfterRequest = 3000; // 请求完成后延迟3秒
    const maxAttempts = 300; // 最长约10分钟
    let attempts = 0;
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 5;
    let shouldContinuePolling = true;

    const poll = async () => {
      if (!shouldContinuePolling) {
        return;
      }

      attempts += 1;
      try {
        const status = await klineTaskService.getPromptAnalysisStatus(taskId);
        consecutiveErrors = 0;

        if (status.status === 'completed' && status.result) {
          const analysisReportHash = status.result;

          let analysisReport: string | null = null;
          try {
            const reportData = await klineDataService.getAnalysisReport(analysisReportHash);
            analysisReport = reportData.analysisReport;
            setAiAnalysis(analysisReport);
          } catch (err: any) {
            console.error('获取分析报告内容失败:', err);
            showToast('获取分析报告内容失败', 'error');
          }

          saveToCache({
            taskId: status.taskId,
            result: analysisReportHash,
            analysisReportHash: analysisReportHash,
            analysisReport: analysisReport,
          });
          if (!silent) {
            showToast('AI分析完成', 'success');
          }
          setAnalyzing(false);
          shouldContinuePolling = false;
          if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
          }
          return;
        }

        if (status.status === 'failed') {
          if (!silent) {
            showToast(status.error || 'AI分析任务失败', 'error');
          }
          setAnalyzing(false);
          shouldContinuePolling = false;
          if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
          }
          return;
        }

        if (status.status === 'pending' || status.status === 'running') {
          // 确保按钮显示分析中状态
          setAnalyzing(true);

          if (attempts >= maxAttempts) {
            showToast('AI分析超时，请稍后重试', 'error');
            setAnalyzing(false);
            shouldContinuePolling = false;
            if (pollTimerRef.current) {
              clearTimeout(pollTimerRef.current);
              pollTimerRef.current = null;
            }
            return;
          }

          if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
          }
          pollTimerRef.current = setTimeout(() => {
            poll();
          }, delayAfterRequest);
          return;
        }
      } catch (err: any) {
        consecutiveErrors += 1;
        console.error(`获取任务状态失败 (连续错误 ${consecutiveErrors}/${maxConsecutiveErrors}):`, err);

        const isNetworkError =
          err.code === 'ECONNABORTED' ||
          err.message?.includes('timeout') ||
          err.message?.includes('网络') ||
          !err.response;

        if (isNetworkError && consecutiveErrors < maxConsecutiveErrors) {
          console.log(`网络错误，继续重试 (${consecutiveErrors}/${maxConsecutiveErrors})`);
          if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
          }
          pollTimerRef.current = setTimeout(() => {
            poll();
          }, delayAfterRequest);
          return;
        }

        if (consecutiveErrors >= maxConsecutiveErrors) {
          showToast('网络连接不稳定，已停止轮询', 'error');
        } else {
          showToast(err.message || '获取任务状态失败', 'error');
        }
        setAnalyzing(false);
        shouldContinuePolling = false;
        if (pollTimerRef.current) {
          clearTimeout(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      }
    };

    // 立即先查一次
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 加载系统提示词列表
  useEffect(() => {
    // 防止在 React.StrictMode 下重复加载
    if (systemPromptsLoadedRef.current) {
      return;
    }
    systemPromptsLoadedRef.current = true;

    const loadSystemPrompts = async () => {
      try {
        setLoadingPrompts(true);
        const result = await adminService.getSystemPrompts(1, 100, undefined, true); // 只加载启用的提示词
        setSystemPrompts(result.prompts);
      } catch (error: any) {
        console.error('加载系统提示词列表失败:', error);
        showToast(error.message || '加载系统提示词列表失败', 'error');
        // 如果加载失败，重置标志以便重试
        systemPromptsLoadedRef.current = false;
      } finally {
        setLoadingPrompts(false);
      }
    };
    loadSystemPrompts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 页面加载时检查缓存
  useEffect(() => {
    if (symbol && bar) {
      const loadCachedData = async () => {
        const cached = findCachedData();
        if (cached && cached.data) {
          console.log('[KlineAIPromptBuilder] 从缓存加载数据', cached);

          // 优先使用完整结构体中的时间范围，否则使用 range 字段
          if (cached.data.startTime && cached.data.endTime) {
            lastPromptRangeRef.current = {
              from: cached.data.startTime,
              to: cached.data.endTime,
            };
          } else if (cached.range) {
            lastPromptRangeRef.current = cached.range;
          }

          // 如果有提示词内容，加载提示词
          if (cached.data.userPrompt || cached.data.systemPrompt) {
            if (cached.data.systemPrompt && cached.data.userPrompt) {
              setSystemPrompt(cached.data.systemPrompt);
              setUserPrompt(cached.data.userPrompt);
              setAiPrompt(cached.data.userPrompt);
              aiPromptRef.current = cached.data.userPrompt;
              setPromptTab('user');
            } else if (cached.data.userPrompt) {
              setUserPrompt(cached.data.userPrompt);
              setAiPrompt(cached.data.userPrompt);
              aiPromptRef.current = cached.data.userPrompt;
            }
          }

          // 如果有完整的结构体信息，记录日志
          if (cached.data.symbol || cached.data.bar || cached.data.promptHash) {
            console.log('[KlineAIPromptBuilder] 从缓存加载完整结构体', {
              symbol: cached.data.symbol,
              bar: cached.data.bar,
              startTime: cached.data.startTime,
              endTime: cached.data.endTime,
              promptHash: cached.data.promptHash ? cached.data.promptHash.substring(0, 8) + '...' : undefined,
            });
            // 恢复 promptHash 以便清除缓存
            if (cached.data.promptHash) {
              setCurrentPromptHash(cached.data.promptHash);
            }
          }

          // 加载分析结果
          // result 只是 hash，analysisReport 才是完整内容
          if (cached.data.analysisReport) {
            setAiAnalysis(cached.data.analysisReport);
          }
          if (cached.data.taskId) {
            setPromptTaskId(cached.data.taskId);
          }
        }
      };
      loadCachedData();
    }
  }, [symbol, bar, findCachedData]);

  // 当 promptTaskId 变化时，启动轮询
  useEffect(() => {
    if (promptTaskId) {
      // 检查是否是从缓存恢复的任务（通过检查缓存中是否已有该 taskId）
      const cached = findCachedData();
      const isFromCache = cached?.data?.taskId === promptTaskId;
      pollPromptAnalysisStatus(promptTaskId, isFromCache);
    }

    // cleanup 函数：当 taskId 变化时，清除之前的轮询
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [promptTaskId, pollPromptAnalysisStatus, findCachedData]);

  // 计算token数量
  const tokenCount = useMemo(() => estimateTokenCount(aiPrompt), [aiPrompt]);

  // 清理轮询定时器
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  const handleGeneratePrompt = async () => {
    if (!symbol || !visibleRange) {
      showToast('请先选择币种并确保K线图已加载', 'error');
      return;
    }

    try {
      setPromptLoading(true);
      const result = await klineDataService.generateAIPrompt(symbol, bar, {
        start: visibleRange.from, // visibleRange已经是秒级时间戳
        end: visibleRange.to,
        systemPromptId: selectedSystemPromptId,
      });

      // 处理完整的返回结构体
      console.log('[KlineAIPromptBuilder] 生成AI提示词成功', {
        systemPrompt: result.systemPrompt ? result.systemPrompt.substring(0, 100) + '...' : undefined,
        userPrompt: result.userPrompt ? result.userPrompt.substring(0, 100) + '...' : undefined,
        symbol: result.symbol,
        bar: result.bar,
        startTime: result.startTime,
        endTime: result.endTime,
        promptHash: result.promptHash.substring(0, 8) + '...',
      });

      // 保存 systemPrompt 和 userPrompt
      setSystemPrompt(result.systemPrompt);
      setUserPrompt(result.userPrompt);
      // 默认显示 userPrompt
      setAiPrompt(result.userPrompt);
      aiPromptRef.current = result.userPrompt;
      setPromptTab('user');

      // 使用返回的时间范围（确保与后端一致）
      lastPromptRangeRef.current = {
        from: result.startTime,
        to: result.endTime,
      };

      // 缓存当前K线视口时间范围（使用 symbol + bar 作为 key）
      try {
        const cacheKey = `kline_visible_range_${symbol}_${bar}`;
        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            from: result.startTime,
            to: result.endTime,
            timestamp: Date.now(),
          }),
        );
        console.log('[KlineAIPromptBuilder] 已缓存K线视口时间范围', {
          symbol: result.symbol,
          bar: result.bar,
          range: { from: result.startTime, to: result.endTime },
        });
      } catch (error) {
        console.error('缓存K线视口时间范围失败:', error);
      }

      // 立即保存完整的结构体到缓存（使用 symbol + bar 作为 key）
      saveToCache({
        // 保存完整的返回结构体
        systemPrompt: result.systemPrompt,
        userPrompt: result.userPrompt,
        symbol: result.symbol,
        bar: result.bar,
        startTime: result.startTime,
        endTime: result.endTime,
        promptHash: result.promptHash,
        // 保留原有的 range 字段以便兼容
        range: { from: result.startTime, to: result.endTime },
      });

      // 更新本地状态（确保与缓存一致）
      setSystemPrompt(result.systemPrompt);
      setUserPrompt(result.userPrompt);
      console.log('[KlineAIPromptBuilder] 已缓存完整结构体', {
        symbol: result.symbol,
        bar: result.bar,
        startTime: result.startTime,
        endTime: result.endTime,
        promptHash: result.promptHash.substring(0, 8) + '...',
      });

      // 生成提示词后，立即检查缓存
      const cached = loadFromCache();
      if (cached) {
        console.log('[KlineAIPromptBuilder] 从缓存加载AI分析结果', cached);
        // result 只是 hash，analysisReport 才是完整内容
        if (cached.analysisReport) {
          setAiAnalysis(cached.analysisReport);
        }
        if (cached.taskId) {
          setPromptTaskId(cached.taskId);
        }
      }

      showToast('AI提示词生成成功', 'success');
    } catch (error: any) {
      console.error('生成AI提示词失败:', error);
      showToast(error.message || '生成AI提示词失败', 'error');
    } finally {
      setPromptLoading(false);
    }
  };

  const handleClearPrompt = () => {
    setAiPrompt('');
    setSystemPrompt('');
    setUserPrompt('');
    setPromptTab('user');
    aiPromptRef.current = '';
    lastPromptRangeRef.current = null;
    setAiAnalysis(null);
    setPromptTaskId(null);
    setCurrentPromptHash(null);
    // 注意：不清除缓存，保留历史记录
  };

  const handleClearCache = async () => {
    if (!currentPromptHash) {
      showToast('没有可清除的缓存', 'warning');
      return;
    }

    try {
      setClearCacheLoading(true);
      // 清除后端Redis缓存
      await klineDataService.clearPromptCache(currentPromptHash);

      // 清除前端localStorage缓存
      if (symbol && bar) {
        // 清除提示词缓存
        const promptCacheKey = getCacheKey();
        if (promptCacheKey) {
          localStorage.removeItem(promptCacheKey);
        }

        // 清除K线视口时间范围缓存
        const visibleRangeCacheKey = `kline_visible_range_${symbol}_${bar}`;
        localStorage.removeItem(visibleRangeCacheKey);

        console.log('[KlineAIPromptBuilder] 已清除前端缓存', {
          promptCacheKey,
          visibleRangeCacheKey,
        });
      }

      // 重置相关状态
      setCurrentPromptHash(null);
      setAiAnalysis(null);
      setPromptTaskId(null);

      showToast('提示词缓存已清除（包括前端缓存）', 'success');
    } catch (error: any) {
      console.error('清除提示词缓存失败:', error);
      showToast(error.message || '清除提示词缓存失败', 'error');
    } finally {
      setClearCacheLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!aiPrompt || !aiPrompt.trim()) {
      showToast('请先生成或输入提示词', 'error');
      return;
    }

    try {
      setAnalyzing(true);
      setAiAnalysis(null);

      // 从缓存中获取完整结构体（包含 promptHash 和元数据）
      const cached = loadFromCache();
      if (!cached || !cached.promptHash) {
        showToast('请先通过生成提示词功能生成提示词，以便获取 promptHash', 'error');
        setAnalyzing(false);
        return;
      }

      const promptHash = cached.promptHash;
      const metadata = {
        symbol: cached.symbol,
        bar: cached.bar,
        startTime: cached.startTime,
        endTime: cached.endTime,
      };

      console.log('[KlineAIPromptBuilder] 从缓存获取完整结构体', {
        promptHash: promptHash.substring(0, 8) + '...',
        symbol: metadata.symbol,
        bar: metadata.bar,
        startTime: metadata.startTime,
        endTime: metadata.endTime,
      });

      // 提交异步任务
      const { taskId } = await klineTaskService.analyzePrompt(promptHash, metadata);
      console.log('提示词AI分析任务已提交', {
        taskId,
        promptHash: promptHash.substring(0, 8) + '...',
        metadata,
      });

      // 保存任务ID到缓存
      saveToCache({ taskId });

      // 设置任务ID，useEffect 会自动启动轮询
      setPromptTaskId(taskId);
    } catch (error: any) {
      console.error('提交AI分析任务失败:', error);
      showToast(error.message || '提交AI分析任务失败', 'error');
      setAnalyzing(false);
    }
  };

  return (
    <div className="pb-4">
      <div className="px-4 py-4">
        <div className="mb-3">
          <h2 className="text-lg font-bold text-white mb-3">构建AI提示词</h2>
          <div className="flex items-center gap-2">
            <SelectDropdown
              value={selectedSystemPromptId?.toString()}
              onChange={(val) => setSelectedSystemPromptId(val ? parseInt(val.toString()) : undefined)}
              options={[
                { value: '', label: '默认提示词' },
                ...systemPrompts.map((prompt) => ({
                  value: prompt.id.toString(),
                  label: prompt.promptId || prompt.title,
                })),
              ]}
              disabled={loadingPrompts}
              loading={loadingPrompts}
              placeholder="默认提示词"
              className="flex-1 mb-0"
            />
            <button
              onClick={handleGeneratePrompt}
              disabled={promptLoading || !symbol || !visibleRange}
              className="px-3 py-1.5 bg-cyan-500/20 text-cyan-200 border border-cyan-500/40 rounded-lg hover:bg-cyan-500/30 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {promptLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>生成中...</span>
                </>
              ) : (
                <span>生成提示词</span>
              )}
            </button>
            <button
              onClick={handleClearCache}
              disabled={clearCacheLoading || !currentPromptHash}
              className="px-3 py-1.5 bg-orange-500/20 text-orange-200 border border-orange-500/40 rounded-lg hover:bg-orange-500/30 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              title="清除Redis缓存中的提示词"
            >
              {clearCacheLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>清除中...</span>
                </>
              ) : (
                <span>清除缓存</span>
              )}
            </button>
          </div>
        </div>

        {visibleRange && (
          <div className="text-xs text-white/60 mb-2">
            当前视口时间范围:{' '}
            {new Date(visibleRange.from * 1000).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' })} 至{' '}
            {new Date(visibleRange.to * 1000).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' })}
          </div>
        )}

        {systemPrompt || userPrompt || aiPrompt ? (
          <div className="space-y-2">
            <div className="text-sm text-white/80 mb-2">生成的AI提示词（可编辑）：</div>
            {/* 标签页（仅在同时有 systemPrompt 和 userPrompt 时显示） */}
            {systemPrompt && userPrompt && (
              <div className="flex gap-2 mb-2">
                <button
                  onClick={() => {
                    setPromptTab('system');
                    setAiPrompt(systemPrompt);
                    aiPromptRef.current = systemPrompt;
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    promptTab === 'system'
                      ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50'
                      : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  系统提示词
                </button>
                <button
                  onClick={() => {
                    setPromptTab('user');
                    setAiPrompt(userPrompt);
                    aiPromptRef.current = userPrompt;
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    promptTab === 'user'
                      ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50'
                      : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  用户提示词
                </button>
                <button
                  onClick={() => {
                    setPromptTab('combined');
                    const combined = `${systemPrompt}\n\n----------\n\n${userPrompt}`;
                    setAiPrompt(combined);
                    aiPromptRef.current = combined;
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    promptTab === 'combined'
                      ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50'
                      : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  完整提示词
                </button>
              </div>
            )}
            <textarea
              value={aiPrompt}
              onChange={(e) => {
                setAiPrompt(e.target.value);
                aiPromptRef.current = e.target.value;
                // 如果正在编辑，更新对应的 prompt
                if (systemPrompt && userPrompt) {
                  if (promptTab === 'system') {
                    setSystemPrompt(e.target.value);
                  } else if (promptTab === 'user') {
                    setUserPrompt(e.target.value);
                  }
                }
              }}
              className="w-full h-64 px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-xs focus:outline-none focus:border-cyan-500/50 resize-y"
              placeholder="AI提示词将显示在这里..."
            />
            <div className="flex items-center justify-between">
              <div className="text-xs text-white/60">共约 {tokenCount.toLocaleString()} 个 token</div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(aiPrompt);
                    showToast('提示词已复制到剪贴板', 'success');
                  }}
                  className="px-3 py-1.5 bg-white/10 text-white border border-white/20 rounded-lg hover:bg-white/20 transition-colors text-sm flex items-center justify-center"
                  title="复制提示词"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </button>
                <button
                  onClick={handleAnalyze}
                  disabled={analyzing || !aiPrompt.trim()}
                  className="px-3 py-1.5 bg-purple-500/20 text-purple-200 border border-purple-500/40 rounded-lg hover:bg-purple-500/30 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {analyzing ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span>分析中...</span>
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                        />
                      </svg>
                      <span>提交分析</span>
                    </>
                  )}
                </button>
                <button
                  onClick={handleClearPrompt}
                  className="px-3 py-1.5 bg-red-500/20 text-red-200 border border-red-500/40 rounded-lg hover:bg-red-500/30 transition-colors text-sm"
                >
                  清空
                </button>
              </div>
            </div>
            {aiAnalysis && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-white/80 font-semibold">AI分析结果：</div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowMonitorDialog(true)}
                      className="px-3 py-1.5 bg-blue-500/20 text-blue-200 border border-blue-500/40 rounded-lg hover:bg-blue-500/30 transition-colors text-sm flex items-center gap-2"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      <span>监控</span>
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(aiAnalysis);
                        showToast('AI分析结果已复制到剪贴板', 'success');
                      }}
                      className="px-3 py-1.5 bg-purple-500/20 text-purple-200 border border-purple-500/40 rounded-lg hover:bg-purple-500/30 transition-colors text-sm flex items-center justify-center"
                      title="复制AI分析结果"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 text-xs max-h-[43rem] overflow-y-auto markdown-content">
                  <div
                    className="text-white/80 leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: markdownToHtml(aiAnalysis),
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-white/50 text-sm">点击「生成提示词」按钮，基于当前K线视口时间范围生成AI分析提示词。</div>
        )}
      </div>
      {showMonitorDialog && (
        <KlineAIMonitorDialog symbol={symbol} bar={bar} onClose={() => setShowMonitorDialog(false)} />
      )}
    </div>
  );
};

export default KlineAIPromptBuilder;
